import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FieldPlacer } from "@/components/fbsign/FieldPlacer";
import { Upload, FileText, Plus, Trash2, Send, Link2, CheckCheck, Loader2, X } from "lucide-react";

const REMINDERS = [["none", "None"], ["daily", "Daily"], ["every_3_days", "Every 3 days"], ["weekly", "Weekly"]];
const lbl = "text-[10px] uppercase tracking-wider text-[var(--ink-2)] block mb-0.5";

// 3-step wizard: Start (upload / template) → Setup (recipients, message, reminders) → Send.
export function NewDocWizard({ onClose, onDone }) {
  const [doc, setDoc] = useState(null);
  const [step, setStep] = useState(1);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="fbsign-wizard">
      <div className={`bg-white rounded-sm w-full ${step === 3 ? "max-w-4xl" : "max-w-2xl"} max-h-[94vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3 text-xs">
            {["Start", "Setup", "Place fields", "Send"].map((s, i) => (
              <span key={s} className={`flex items-center gap-1.5 ${step === i + 1 ? "text-[var(--navy)] font-medium" : "text-[var(--ink-2)]"}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step >= i + 1 ? "bg-[var(--navy)] text-white" : "bg-[var(--bg-2)]"}`}>{i + 1}</span>{s}
              </span>
            ))}
          </div>
          <button data-testid="fbsign-wizard-close" onClick={() => { onClose(); if (doc) onDone(); }} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={15} /></button>
        </div>
        <div className="p-5">
          {step === 1 && <StartStep onCreated={(d) => { setDoc(d); setStep(2); }} />}
          {step === 2 && doc && <SetupStep doc={doc} onSaved={(d) => { setDoc(d); setStep(3); }} />}
          {step === 3 && doc && <FieldPlacer doc={doc} onBack={() => setStep(2)} onSaved={(d) => { setDoc(d); setStep(4); }} />}
          {step === 4 && doc && <SendStep doc={doc} onBack={() => setStep(3)} onFinished={() => { onDone(); onClose(); }} />}
        </div>
      </div>
    </div>
  );
}

function StartStep({ onCreated }) {
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("");
  useEffect(() => { api.get("/templates").then((r) => setTemplates(r.data || [])).catch(() => {}); }, []);

  async function upload(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { onCreated((await api.post("/fbsign/upload", fd)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  }
  async function useTemplate() {
    if (!tid) return;
    setBusy(true);
    try { onCreated((await api.post("/fbsign/from-template", { template_id: tid })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not use template"); }
    finally { setBusy(false); }
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <label className={`border-2 border-dashed border-[var(--line)] rounded-sm p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[var(--navy)] transition-colors ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="fbsign-upload-zone">
        {busy ? <Loader2 size={26} className="animate-spin text-[var(--navy)]" /> : <Upload size={26} className="text-[var(--navy)]" />}
        <div className="font-medium text-sm mt-2">Upload a document</div>
        <div className="text-xs text-[var(--ink-2)] mt-0.5">PDF or Word (.docx), up to 20 MB</div>
        <input data-testid="fbsign-upload-input" type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      <div className="border border-[var(--line)] rounded-sm p-6 flex flex-col justify-center">
        <div className="flex items-center gap-1.5 font-medium text-sm mb-2"><FileText size={16} className="text-[var(--navy)]" /> Use a saved template</div>
        <select data-testid="fbsign-template-select" value={tid} onChange={(e) => setTid(e.target.value)} className="input-slim w-full text-xs mb-2">
          <option value="">— Pick a template —</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{`${t.category} · ${t.name}`}</option>)}
        </select>
        <button data-testid="fbsign-template-use" onClick={useTemplate} disabled={!tid || busy} className="btn-primary text-xs !py-1.5">Use template</button>
      </div>
    </div>
  );
}

function SetupStep({ doc, onSaved }) {
  const [name, setName] = useState(doc.name || "");
  const [recips, setRecips] = useState(doc.recipients?.length ? doc.recipients.map(({ name, email }) => ({ name, email })) : [{ name: "", email: "" }]);
  const [message, setMessage] = useState(doc.message || "");
  const [reminder, setReminder] = useState(doc.reminder || "none");
  const [busy, setBusy] = useState(false);
  const setR = (i, k, v) => setRecips((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  async function save() {
    const clean = recips.filter((r) => r.name.trim() || r.email.trim());
    if (!name.trim()) { toast.error("Give the document a name"); return; }
    if (!clean.length || clean.some((r) => !r.name.trim() || !/^\S+@\S+\.\S+$/.test(r.email.trim()))) { toast.error("Each recipient needs a name and a valid email"); return; }
    setBusy(true);
    try { onSaved((await api.patch(`/fbsign/${doc.id}`, { name: name.trim(), recipients: clean, message, reminder })).data); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Please check the recipient details"); }
    finally { setBusy(false); }
  }
  return (
    <div className="space-y-4">
      <div>
        <label className={lbl}>Document name</label>
        <input data-testid="fbsign-doc-name" value={name} onChange={(e) => setName(e.target.value)} className="input-slim w-full text-sm" />
        <div className="text-[10px] text-[var(--ink-2)] mt-0.5">{doc.file_name ? `File: ${doc.file_name}` : "From template"}</div>
      </div>
      <div>
        <label className={lbl}>Recipients</label>
        <div className="space-y-1.5">
          {recips.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <input data-testid={`fbsign-recipient-name-${i}`} value={r.name} onChange={(e) => setR(i, "name", e.target.value)} placeholder="Full name" className="input-slim text-xs flex-1" />
              <input data-testid={`fbsign-recipient-email-${i}`} value={r.email} onChange={(e) => setR(i, "email", e.target.value)} placeholder="email@example.com" className="input-slim text-xs flex-1" />
              <button data-testid={`fbsign-recipient-remove-${i}`} onClick={() => setRecips((rs) => rs.filter((_, j) => j !== i))} disabled={recips.length === 1} className="p-1 text-[var(--ink-2)] hover:text-[var(--err)] disabled:opacity-30"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        <button data-testid="fbsign-add-recipient" onClick={() => setRecips((rs) => [...rs, { name: "", email: "" }])} className="mt-1.5 text-xs text-[var(--navy)] flex items-center gap-1 hover:underline"><Plus size={12} /> Add recipient</button>
      </div>
      <div>
        <label className={lbl}>Message to recipient (optional)</label>
        <textarea data-testid="fbsign-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className="input-slim w-full text-xs" placeholder="e.g. Please review and sign by Friday." />
      </div>
      <div>
        <label className={lbl}>Reminders</label>
        <div className="flex gap-1.5 flex-wrap">
          {REMINDERS.map(([k, l]) => (
            <button key={k} data-testid={`fbsign-reminder-${k}`} onClick={() => setReminder(k)}
              className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${reminder === k ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--line)] hover:border-[var(--navy)]"}`}>{l}</button>
          ))}
        </div>
        <div className="text-[10px] text-[var(--ink-2)] mt-1">Reminders go out at 09:00 UK time, Monday to Friday, until signed.</div>
      </div>
      <div className="flex justify-end">
        <button data-testid="fbsign-setup-next" onClick={save} disabled={busy} className="btn-primary text-xs !py-1.5 !px-3 flex items-center gap-1.5">{busy && <Loader2 size={12} className="animate-spin" />} Next: Place fields</button>
      </div>
    </div>
  );
}

function SendStep({ doc, onBack, onFinished }) {
  const [busy, setBusy] = useState("");
  const [links, setLinks] = useState(null);

  async function send(mode) {
    setBusy(mode);
    try {
      const r = (await api.post(`/fbsign/${doc.id}/send`, { mode })).data;
      setLinks(r.links);
      if (mode === "email") toast.success(r.emailed ? `Sent to ${r.emailed} recipient(s)` : "Marked in progress — email could not be sent, copy the links instead");
      else if (mode === "mark_sent") toast.success("Marked as sent");
      return r.links;
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); return null; }
    finally { setBusy(""); }
  }
  async function copyLinks() {
    const ls = links || (await send("link"));
    if (!ls) return;
    try { await navigator.clipboard.writeText(ls.map((l) => (ls.length > 1 ? `${l.name}: ${l.url}` : l.url)).join("\n")); } catch (e) {}
    toast.success(ls.length > 1 ? "Signing links copied" : "Signing link copied");
  }
  const Btn = ({ mode, icon: Icon, label, onClick, primary, tid }) => (
    <button data-testid={tid} onClick={onClick} disabled={!!busy}
      className={`${primary ? "btn-primary" : "btn-ghost"} flex items-center justify-center gap-1.5 text-xs !py-2 flex-1`}>
      {busy === mode ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />} {label}
    </button>
  );
  return (
    <div className="space-y-4">
      <div className="text-sm"><b>{doc.name}</b> → {doc.recipients.map((r) => `${r.name} <${r.email}>`).join(", ")}</div>
      <div className="text-xs text-[var(--ink-2)]" data-testid="fbsign-send-fields-summary">{doc.fields?.length ? `${doc.fields.length} signing box(es) placed.` : "No boxes placed — signatures go on the certificate page."}</div>
      <div className="flex gap-2 flex-wrap">
        <Btn tid="fbsign-send-now" mode="email" icon={Send} label="Send now (email)" primary onClick={() => send("email")} />
        <Btn tid="fbsign-copy-link" mode="link" icon={Link2} label="Copy link" onClick={copyLinks} />
        <Btn tid="fbsign-mark-sent" mode="mark_sent" icon={CheckCheck} label="Mark as sent" onClick={() => send("mark_sent")} />
      </div>
      {links && (
        <div className="border border-[var(--line)] rounded-sm p-3 bg-[var(--bg-2)]" data-testid="fbsign-links">
          <div className={lbl}>Secure signing links</div>
          {links.map((l) => (
            <div key={l.url} className="text-xs flex items-center gap-2 py-0.5">
              <span className="w-28 truncate">{l.name}</span>
              <input readOnly value={l.url} onClick={(e) => e.target.select()} className="input-slim text-[11px] flex-1 font-mono-data" />
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between">
        {!links ? <button onClick={onBack} className="btn-ghost text-xs !py-1.5 !px-3">← Back</button> : <span />}
        {links && <button data-testid="fbsign-finish" onClick={onFinished} className="btn-primary text-xs !py-1.5 !px-3">Done</button>}
      </div>
    </div>
  );
}
